import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, FormGroup, Input, Label, Table } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import SettingsPanel from "../../components/SettingsPanel";
import LoadingSpinner from "../../../../Components/Common/Spinner";
import AppModal from "../../../../Components/Common/AppModal";
import { FetchAPI } from "../../../../helpers/APIdata/fetchAPI";
import { getStaffList } from "../../../../store/staff/action";

// Service layer — the server scopes everything to the logged-in user's OWN
// company (Company_Id from the token), so the front end never sends a company id.
const listRecipients = () => FetchAPI("/companybillingrecipient/list", "GET");
const createRecipient = (rows) =>
  FetchAPI("/companybillingrecipient/create", "POST", rows);
const updateRecipient = (rows) =>
  FetchAPI("/companybillingrecipient/update", "PUT", rows);
const deleteRecipient = (ids) =>
  FetchAPI("/companybillingrecipient/delete", "DELETE", ids);

const staffLabel = (s) =>
  s?.FullName ||
  s?.UserFullName ||
  [s?.FirstName, s?.LastName].filter(Boolean).join(" ") ||
  s?.Email ||
  `User ${s?.Id}`;

const emptyForm = {
  Id: null,
  RecipientType: "TO",
  Source: "user", // "user" | "custom"
  User_Id: "",
  EmailAddress: "",
};

// Settings > Billing > Invoice Recipients.
// Chooses who receives the CRMit subscription invoice for this company. Each row
// is either linked to a system user (the invoice follows their login email) or a
// custom address (e.g. a Xero inbound address or the customer's accountant).
// At least one TO recipient must always exist.
export default function InvoiceRecipients() {
  const dispatch = useDispatch();

  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const staffData = useSelector((state) => state.Staff.staffList?.staff_list);
  const staffList = useMemo(
    () => (Array.isArray(staffData) ? staffData : []),
    [staffData]
  );

  const loadRecipients = async () => {
    setLoading(true);
    try {
      const res = await listRecipients();
      if (res?.status === 200) {
        setRecipients(res.companybillingrecipient_list || []);
      } else {
        toast.error(res?.message || "Failed to load recipients");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecipients();
    dispatch(getStaffList());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toCount = useMemo(
    () =>
      recipients.filter((r) => String(r.RecipientType).toUpperCase() === "TO")
        .length,
    [recipients]
  );

  const openAdd = () => {
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setForm({
      Id: row.Id,
      RecipientType: String(row.RecipientType).toUpperCase() === "CC" ? "CC" : "TO",
      Source: row.User_Id ? "user" : "custom",
      User_Id: row.User_Id || "",
      EmailAddress: row.EmailAddress || "",
    });
    setModalOpen(true);
  };

  const buildPayloadRow = () => {
    const row = { RecipientType: form.RecipientType };
    if (form.Source === "user") {
      row.User_Id = form.User_Id ? Number(form.User_Id) : null;
      row.EmailAddress = null;
    } else {
      row.EmailAddress = form.EmailAddress ? form.EmailAddress.trim() : "";
      row.User_Id = null;
    }
    if (form.Id) row.Id = form.Id;
    return row;
  };

  const validateForm = () => {
    if (form.Source === "user" && !form.User_Id) {
      toast.error("Choose a user");
      return false;
    }
    if (form.Source === "custom") {
      const email = (form.EmailAddress || "").trim();
      if (!email) {
        toast.error("Enter an email address");
        return false;
      }
      // simple sanity check; the server validates properly
      if (!/^\S+@\S+\.\S+$/.test(email)) {
        toast.error("Enter a valid email address");
        return false;
      }
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const row = buildPayloadRow();
      const res = form.Id
        ? await updateRecipient([row])
        : await createRecipient([row]);
      if (res?.status === 200) {
        toast.success(form.Id ? "Recipient updated" : "Recipient added");
        setModalOpen(false);
        loadRecipients();
      } else {
        toast.error(res?.message || "Save failed");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (
      String(row.RecipientType).toUpperCase() === "TO" &&
      toCount <= 1
    ) {
      toast.error(
        "At least one TO recipient is required. Add another TO recipient before removing this one."
      );
      return;
    }
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove ${row.EmailResolved || "this recipient"}?`)) {
      return;
    }
    const res = await deleteRecipient([row.Id]);
    if (res?.status === 200) {
      toast.success("Recipient removed");
      loadRecipients();
    } else {
      toast.error(res?.message || "Remove failed");
    }
  };

  const renderRow = (row) => (
    <tr key={row.Id}>
      <td>
        <span className="fw-semibold">{row.EmailResolved || "-"}</span>
        {row.User_Id ? (
          <div className="text-muted small">
            Linked to {row.User_UserFullName || `user #${row.User_Id}`}
          </div>
        ) : (
          <div className="text-muted small">Custom address</div>
        )}
      </td>
      <td>
        <Badge color={row.User_Id ? "info" : "secondary"} className="me-1">
          {row.User_Id ? "System user" : "Custom email"}
        </Badge>
      </td>
      <td className="text-end">
        <Button
          size="sm"
          color="light"
          className="me-1"
          onClick={() => openEdit(row)}
        >
          <i className="ri-pencil-line" />
        </Button>
        <Button size="sm" color="light" onClick={() => handleDelete(row)}>
          <i className="ri-delete-bin-line text-danger" />
        </Button>
      </td>
    </tr>
  );

  const toRows = recipients.filter(
    (r) => String(r.RecipientType).toUpperCase() === "TO"
  );
  const ccRows = recipients.filter(
    (r) => String(r.RecipientType).toUpperCase() === "CC"
  );

  return (
    <SettingsPanel
      title="Invoice Recipients"
      description="Choose who receives your CRMit subscription invoice. Link a team member (the invoice follows their email) or add a custom address such as a Xero inbox or your accountant. At least one TO recipient is required."
      actions={
        <Button color="primary" onClick={openAdd}>
          <i className="ri-add-line align-bottom me-1" />
          Add recipient
        </Button>
      }
    >
      {loading && recipients.length === 0 ? (
        <LoadingSpinner />
      ) : (
        <>
          <h5 className="mb-2">To</h5>
          <div className="table-responsive mb-4">
            <Table className="align-middle table-nowrap mb-0">
              <thead className="text-muted">
                <tr>
                  <th>Recipient</th>
                  <th>Kind</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {toRows.length ? (
                  toRows.map(renderRow)
                ) : (
                  <tr>
                    <td colSpan={3} className="text-muted">
                      No TO recipients yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <h5 className="mb-2">Cc</h5>
          <div className="table-responsive">
            <Table className="align-middle table-nowrap mb-0">
              <thead className="text-muted">
                <tr>
                  <th>Recipient</th>
                  <th>Kind</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {ccRows.length ? (
                  ccRows.map(renderRow)
                ) : (
                  <tr>
                    <td colSpan={3} className="text-muted">
                      No CC recipients.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </>
      )}

      <AppModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={form.Id ? "Edit recipient" : "Add recipient"}
        size="sm"
        loading={saving}
        onSubmit={handleSave}
        submitLabel={form.Id ? "Save" : "Add"}
      >
        <FormGroup>
          <Label>Send as</Label>
          <Input
            type="select"
            value={form.RecipientType}
            onChange={(e) =>
              setForm((f) => ({ ...f, RecipientType: e.target.value }))
            }
          >
            <option value="TO">To</option>
            <option value="CC">Cc</option>
          </Input>
        </FormGroup>

        <FormGroup>
          <Label>Recipient type</Label>
          <div>
            <FormGroup check inline>
              <Input
                type="radio"
                name="recipientSource"
                id="source-user"
                checked={form.Source === "user"}
                onChange={() => setForm((f) => ({ ...f, Source: "user" }))}
              />
              <Label check for="source-user">
                Team member
              </Label>
            </FormGroup>
            <FormGroup check inline>
              <Input
                type="radio"
                name="recipientSource"
                id="source-custom"
                checked={form.Source === "custom"}
                onChange={() => setForm((f) => ({ ...f, Source: "custom" }))}
              />
              <Label check for="source-custom">
                Custom email
              </Label>
            </FormGroup>
          </div>
        </FormGroup>

        {form.Source === "user" ? (
          <FormGroup>
            <Label>Team member</Label>
            <Input
              type="select"
              value={form.User_Id}
              onChange={(e) =>
                setForm((f) => ({ ...f, User_Id: e.target.value }))
              }
            >
              <option value="">Choose a team member…</option>
              {staffList.map((s) => (
                <option key={s.Id} value={s.Id}>
                  {staffLabel(s)}
                  {s.Email ? ` (${s.Email})` : ""}
                </option>
              ))}
            </Input>
            <small className="text-muted">
              The invoice follows this person&apos;s login email if it changes.
            </small>
          </FormGroup>
        ) : (
          <FormGroup>
            <Label>Email address</Label>
            <Input
              type="email"
              placeholder="accounts@example.com"
              value={form.EmailAddress}
              onChange={(e) =>
                setForm((f) => ({ ...f, EmailAddress: e.target.value }))
              }
            />
            <small className="text-muted">
              For a Xero inbound address or an external accountant.
            </small>
          </FormGroup>
        )}
      </AppModal>
    </SettingsPanel>
  );
}
